import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { sendPasswordResetEmail } from 'firebase/auth'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { auth } from '@/lib/firebase'
import { adminResetPassword } from '@/lib/admin-functions'
import type { User } from '@/types'

type LinkState =
  | { status: 'loading' }
  | { status: 'ready'; link: string }
  | { status: 'failed' }

interface ResetPasswordDialogProps {
  /** The user to reset; the dialog is open while this is set. */
  user: User | null
  onClose: () => void
}

/**
 * Password reset for an admin-selected user.
 *
 * As WEB_ADMIN_SYSTEM_DESIGN.md describes, `adminResetPassword` (admin-verified,
 * server-side) generates a reset link and returns it; nothing emails it. The dialog
 * shows that link for the admin to pass on, and offers Firebase's own reset email as
 * the alternative, so the screen never claims an email went out when none did.
 */
export function ResetPasswordDialog({ user, onClose }: ResetPasswordDialogProps) {
  const { t } = useTranslation()
  const [linkState, setLinkState] = useState<LinkState>({ status: 'loading' })
  const [copied, setCopied] = useState(false)
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')

  useEffect(() => {
    if (!user) return
    let cancelled = false
    adminResetPassword({ uid: user.id })
      .then((result) => {
        if (cancelled) return
        const link = result.data?.resetLink
        setLinkState(link ? { status: 'ready', link } : { status: 'failed' })
      })
      .catch((error) => {
        console.error('adminResetPassword failed:', error)
        if (!cancelled) setLinkState({ status: 'failed' })
      })
    return () => {
      cancelled = true
    }
  }, [user])

  const handleClose = () => {
    setLinkState({ status: 'loading' })
    setCopied(false)
    setEmailState('idle')
    onClose()
  }

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const sendEmail = async () => {
    if (!user?.email) return
    setEmailState('sending')
    try {
      await sendPasswordResetEmail(auth, user.email)
      setEmailState('sent')
    } catch (error) {
      console.error('sendPasswordResetEmail failed:', error)
      setEmailState('failed')
    }
  }

  return (
    <Modal open={!!user} onClose={handleClose} title={t('users.reset.title')}>
      {user && (
        <div className="space-y-4 text-sm text-gray-700">
          <p>{t('users.reset.intro', { name: user.displayName, email: user.email })}</p>

          {linkState.status === 'loading' && <p className="text-gray-500">{t('users.reset.generating')}</p>}

          {linkState.status === 'ready' && (
            <div className="space-y-2">
              <label htmlFor="reset-link" className="block font-medium text-gray-900">
                {t('users.reset.linkLabel')}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="reset-link"
                  readOnly
                  value={linkState.link}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full min-w-0 rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
                />
                <Button variant="secondary" onClick={() => copyLink(linkState.link)}>
                  {copied ? t('users.reset.copied') : t('users.reset.copy')}
                </Button>
              </div>
              <p className="text-xs text-gray-500">{t('users.reset.linkHint')}</p>
            </div>
          )}

          {linkState.status === 'failed' && (
            <div className="rounded-lg bg-red-50 p-3 text-red-700">{t('users.reset.linkFailed')}</div>
          )}

          <div className="space-y-2 border-t border-gray-200 pt-4">
            <p>{t('users.reset.emailOption')}</p>
            <Button
              variant="secondary"
              onClick={sendEmail}
              disabled={!user.email || emailState === 'sending' || emailState === 'sent'}
            >
              {emailState === 'sending' ? t('users.reset.sendingEmail') : t('users.reset.sendEmail')}
            </Button>
            {emailState === 'sent' && (
              <p className="text-green-700">{t('users.reset.emailSent', { email: user.email })}</p>
            )}
            {emailState === 'failed' && <p className="text-red-700">{t('users.reset.emailFailed')}</p>}
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleClose}>{t('common.close')}</Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
